import React, { useState, useEffect } from 'react';
import apiClient from '../apiClient';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useShop } from '../context/ShopContext';
import EmptyState from "./EmptyState";
import ResponsiveTable from "./ResponsiveTable";

/* ── helpers ── */
const fmt = (v) => `₹${parseFloat(v) || 0}`;
const fmtDate = (d) => d ? d.split("T")[0].split("-").reverse().join("-") : "—";
const sum = (arr, key) => arr.reduce((s, r) => s + (parseFloat(r[key]) || 0), 0);

/* ── Column definitions for the desktop/tablet table ── */
const COLUMNS = [
  { key: "sno",            label: "S.No.",          align: "center", width: 48 },
  { key: "salesDate",      label: "Date",            render: (v) => fmtDate(v) },
  { key: "english",        label: "अंग्रेज़ी",         align: "right",  render: fmt },
  { key: "desi",           label: "देसी",             align: "right",  render: fmt },
  { key: "sales",          label: "Net Sales",       align: "right",  render: fmt },
  { key: "exp",            label: "Total Exp.",      align: "right",  render: fmt },
  { key: "debit",          label: "Debit",           align: "right",  render: fmt },
  { key: "credit",         label: "Credit",          align: "right",  render: fmt },
  { key: "salary",         label: "Salary",          align: "right",  render: fmt },
  { key: "commissionValue",label: "Commission",      align: "right",  render: (v) => `₹${parseFloat(v) || 0}` },
  { key: "rdCredit",       label: "R.D. Credit",     align: "right",  render: fmt },
  { key: "rdDebit",        label: "R.D. Debit",      align: "right",  render: fmt },
  { key: "pPayCredit",     label: "PhonePe Credit",  align: "right",  render: fmt },
  { key: "amount",         label: "In Hand Cash",    align: "right",  render: fmt },
];

/* ── Mobile card renderer — called once per row ── */
const SalesCard = ({ row, index }) => (
  <div className="rsp-card">
    <div className="rsp-card-header">
      <span className="rsp-card-title">📅 {fmtDate(row.salesDate)}</span>
      <span className="rsp-card-badge">#{index + 1}</span>
    </div>
    <div className="rsp-card-body">

      <div className="rsp-section-label">Sales</div>
      <div className="rsp-stat-grid rsp-stat-grid-3">
        <div className="rsp-stat">
          <span className="rsp-stat-label">अंग्रेज़ी</span>
          <span className="rsp-stat-value rsp-amount">{fmt(row.english)}</span>
        </div>
        <div className="rsp-stat">
          <span className="rsp-stat-label">देसी</span>
          <span className="rsp-stat-value rsp-amount">{fmt(row.desi)}</span>
        </div>
        <div className="rsp-stat">
          <span className="rsp-stat-label">Net Sales</span>
          <span className="rsp-stat-value rsp-amount">{fmt(row.sales)}</span>
        </div>
      </div>

      <div className="rsp-section-label">Outflow</div>
      <div className="rsp-stat-grid rsp-stat-grid-3">
        <div className="rsp-stat">
          <span className="rsp-stat-label">Expense</span>
          <span className="rsp-stat-value">{fmt(row.exp)}</span>
        </div>
        <div className="rsp-stat">
          <span className="rsp-stat-label">Debit</span>
          <span className="rsp-stat-value">{fmt(row.debit)}</span>
        </div>
        <div className="rsp-stat">
          <span className="rsp-stat-label">Credit</span>
          <span className="rsp-stat-value">{fmt(row.credit)}</span>
        </div>
      </div>

      <div className="rsp-section-label">Staff &amp; Misc</div>
      <div className="rsp-stat-grid rsp-stat-grid-3">
        <div className="rsp-stat">
          <span className="rsp-stat-label">Salary</span>
          <span className="rsp-stat-value">{fmt(row.salary)}</span>
        </div>
        <div className="rsp-stat">
          <span className="rsp-stat-label">Commis.</span>
          <span className="rsp-stat-value">{fmt(row.commissionValue)}</span>
        </div>
        <div className="rsp-stat">
          <span className="rsp-stat-label">PhonePe</span>
          <span className="rsp-stat-value">{fmt(row.pPayCredit)}</span>
        </div>
      </div>

      <div className="rsp-section-label">Rate Diff.</div>
      <div className="rsp-stat-grid rsp-stat-grid-2">
        <div className="rsp-stat">
          <span className="rsp-stat-label">R.D. Credit</span>
          <span className="rsp-stat-value">{fmt(row.rdCredit)}</span>
        </div>
        <div className="rsp-stat">
          <span className="rsp-stat-label">R.D. Debit</span>
          <span className="rsp-stat-value">{fmt(row.rdDebit)}</span>
        </div>
      </div>

      <div className="rsp-card-accent">
        <span className="rsp-card-accent-label">In Hand Cash</span>
        <span className="rsp-card-accent-value">{fmt(row.amount)}</span>
      </div>
    </div>
  </div>
);

/* ── Mobile totals card ── */
const SalesTotalsCard = ({ data }) => {
  const t = {
    english:         sum(data, "english").toFixed(2),
    desi:            sum(data, "desi").toFixed(2),
    sales:           sum(data, "sales").toFixed(2),
    exp:             sum(data, "exp").toFixed(2),
    debit:           sum(data, "debit").toFixed(2),
    credit:          sum(data, "credit").toFixed(2),
    salary:          sum(data, "salary").toFixed(2),
    commissionValue: sum(data, "commissionValue").toFixed(2),
    rdCredit:        sum(data, "rdCredit").toFixed(2),
    rdDebit:         sum(data, "rdDebit").toFixed(2),
    pPayCredit:      sum(data, "pPayCredit").toFixed(2),
    amount:          sum(data, "amount").toFixed(2),
  };
  return (
    <div className="rsp-totals-card">
      <div className="rsp-totals-title">Grand Total</div>

      <div className="rsp-totals-grid rsp-totals-grid-3" style={{ marginBottom: 7 }}>
        <div className="rsp-total-stat">
          <span className="rsp-total-stat-label">अंग्रेज़ी</span>
          <span className="rsp-total-stat-value">₹{t.english}</span>
        </div>
        <div className="rsp-total-stat">
          <span className="rsp-total-stat-label">देसी</span>
          <span className="rsp-total-stat-value">₹{t.desi}</span>
        </div>
        <div className="rsp-total-stat">
          <span className="rsp-total-stat-label">Net Sales</span>
          <span className="rsp-total-stat-value">₹{t.sales}</span>
        </div>
      </div>

      <div className="rsp-totals-grid rsp-totals-grid-3" style={{ marginBottom: 7 }}>
        <div className="rsp-total-stat">
          <span className="rsp-total-stat-label">Expense</span>
          <span className="rsp-total-stat-value">₹{t.exp}</span>
        </div>
        <div className="rsp-total-stat">
          <span className="rsp-total-stat-label">Debit</span>
          <span className="rsp-total-stat-value">₹{t.debit}</span>
        </div>
        <div className="rsp-total-stat">
          <span className="rsp-total-stat-label">Credit</span>
          <span className="rsp-total-stat-value">₹{t.credit}</span>
        </div>
      </div>

      <div className="rsp-totals-grid rsp-totals-grid-3" style={{ marginBottom: 7 }}>
        <div className="rsp-total-stat">
          <span className="rsp-total-stat-label">Salary</span>
          <span className="rsp-total-stat-value">₹{t.salary}</span>
        </div>
        <div className="rsp-total-stat">
          <span className="rsp-total-stat-label">Commis.</span>
          <span className="rsp-total-stat-value">₹{t.commissionValue}</span>
        </div>
        <div className="rsp-total-stat">
          <span className="rsp-total-stat-label">PhonePe</span>
          <span className="rsp-total-stat-value">₹{t.pPayCredit}</span>
        </div>
      </div>

      <div className="rsp-totals-grid rsp-totals-grid-2" style={{ marginBottom: 7 }}>
        <div className="rsp-total-stat">
          <span className="rsp-total-stat-label">R.D. Credit</span>
          <span className="rsp-total-stat-value">₹{t.rdCredit}</span>
        </div>
        <div className="rsp-total-stat">
          <span className="rsp-total-stat-label">R.D. Debit</span>
          <span className="rsp-total-stat-value">₹{t.rdDebit}</span>
        </div>
      </div>

      <div className="rsp-totals-accent">
        <span className="rsp-totals-accent-label">Total In Hand</span>
        <span className="rsp-totals-accent-value">₹{t.amount}</span>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════ */
const Salesreport = () => {
  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const { shopName, shopId, userId, companyId } = useShop();

  const showToast = (message, type = "error") => {
    toast.dismiss();
    if (type.toLowerCase() === "error") toast.error(message);
    else if (type.toLowerCase() === "success") toast.success(message);
  };

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setFromDate(today);
    setToDate(today);
  }, []);

  const fetchStockData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/Liquor/GetSalesAmount', {
        shopId: shopId,
        fromDate: new Date(fromDate).toISOString(),
        toDate: new Date(toDate).toISOString(),
        companyId: companyId,
      });
      if (response.data && response.data.data) {
        setStockData(response.data.data);
        setIsDataFetched(true);
      } else {
        setStockData([]);
        setIsDataFetched(false);
      }
    } catch (err) {
      console.error('Error fetching stock data:', err);
      setError('Failed to fetch stock data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!fromDate || !toDate) { setError('Please select both from and to dates.'); return; }
    fetchStockData();
  };

  const exportToExcel = () => {
    if (!stockData || stockData.length === 0) { showToast("No data to export!", "error"); return; }
    try {
      const exportData = stockData.map((item, index) => ({
        "S.No.": index + 1,
        "Sales Date": fmtDate(item.salesDate),
        "अंग्रेज़ी": parseFloat(item.english) || 0,
        "देसी": parseFloat(item.desi) || 0,
        "Total Net Sales": parseFloat(item.sales) || 0,
        "Total exp": parseFloat(item.exp) || 0,
        "Debit": parseFloat(item.debit) || 0,
        "Credit": parseFloat(item.credit) || 0,
        "Salary": parseFloat(item.salary) || 0,
        "Commission": parseFloat(item.commissionValue) || 0,
        "R.D.Credit": parseFloat(item.rdCredit) || 0,
        "R.D. Debit": parseFloat(item.rdDebit) || 0,
        "Phonepay Credit": parseFloat(item.pPayCredit) || 0,
        "In hand Cash": parseFloat(item.amount) || 0,
      }));
      exportData.push({
        "S.No.": "Total",
        "Sales Date": "",
        "अंग्रेज़ी": sum(stockData, "english").toFixed(2),
        "देसी": sum(stockData, "desi").toFixed(2),
        "Total Net Sales": sum(stockData, "sales").toFixed(2),
        "Total exp": sum(stockData, "exp").toFixed(2),
        "Debit": sum(stockData, "debit").toFixed(2),
        "Credit": sum(stockData, "credit").toFixed(2),
        "Salary": sum(stockData, "salary").toFixed(2),
        "Commission": sum(stockData, "commissionValue").toFixed(2),
        "R.D. Credit": sum(stockData, "rdCredit").toFixed(2),
        "R.D. debit": sum(stockData, "rdDebit").toFixed(2),
        "Phonepay Credit": sum(stockData, "pPayCredit").toFixed(2),
        "In hand Cash": sum(stockData, "amount").toFixed(2),
      });
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Report");
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
        "Sales_Report.xlsx"
      );
      showToast("Data exported successfully!", "success");
    } catch (error) {
      console.error("Error exporting data:", error);
      showToast("Failed to export data.", "error");
    }
  };

  const totalPages = Math.ceil(stockData ? stockData.length / entriesPerPage : 0);

  /* ── Build table data (with sno added) ── */
  const tableData = stockData
    ? stockData.map((item, i) => ({ ...item, sno: i + 1 }))
    : [];

  /* ── Footer totals object keyed by column.key ── */
  const footerTotals = stockData ? {
    sno:             "",
    salesDate:       "",
    english:         `₹${sum(stockData, "english").toFixed(2)}`,
    desi:            `₹${sum(stockData, "desi").toFixed(2)}`,
    sales:           `₹${sum(stockData, "sales").toFixed(2)}`,
    exp:             `₹${sum(stockData, "exp").toFixed(2)}`,
    debit:           `₹${sum(stockData, "debit").toFixed(2)}`,
    credit:          `₹${sum(stockData, "credit").toFixed(2)}`,
    salary:          `₹${sum(stockData, "salary").toFixed(2)}`,
    commissionValue: `₹${sum(stockData, "commissionValue").toFixed(2)}`,
    rdCredit:        `₹${sum(stockData, "rdCredit").toFixed(2)}`,
    rdDebit:         `₹${sum(stockData, "rdDebit").toFixed(2)}`,
    pPayCredit:      `₹${sum(stockData, "pPayCredit").toFixed(2)}`,
    amount:          `₹${sum(stockData, "amount").toFixed(2)}`,
  } : null;

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <ToastContainer />

      <div className="d-flex flex-column flex-column-fluid">
        {/* Toolbar */}
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-gray-900 fw-bold fs-3 flex-column justify-content-center my-0">
                Sales Report
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="index.html" className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-500 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">Reports</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-fluid">
            <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
              <div className="col-xl-12">
                <div className="card card-flush">
                  <div className="card-body pt-6">

                    {/* ── Filter Form ── */}
                    <form onSubmit={handleSubmit} style={{ marginBottom: 20 }}>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 12,
                          alignItems: "flex-end",
                        }}
                      >
                        <div style={{ flex: "1 1 150px", minWidth: 130 }}>
                          <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>
                            From Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                          />
                        </div>
                        <div style={{ flex: "1 1 150px", minWidth: 130 }}>
                          <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>
                            To Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                          />
                        </div>
                        <div style={{ flexShrink: 0 }}>
                          <button type="submit" className="btn btn-primary">
                            + Get Data
                          </button>
                        </div>
                      </div>
                    </form>

                    {error && (
                      <p style={{ color: "red", marginBottom: 12 }}>Failed to fetch data.</p>
                    )}

                    {/* ── Table / Cards ── */}
                    {stockData === null && !loading ? null : (
                      !loading && stockData && stockData.length === 0
                        ? <EmptyState />
                        : (
                          <>
                            <ResponsiveTable
                              columns={COLUMNS}
                              data={tableData}
                              loading={loading}
                              footer={footerTotals}
                              footerLabel="Total"
                              maxHeight="calc(100vh - 320px)"
                              renderCard={(row, i) => <SalesCard key={i} row={row} index={i} />}
                              renderTotalsCard={stockData ? () => <SalesTotalsCard data={stockData} /> : undefined}
                            />

                            {/* ── Pagination + Export ── */}
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 10,
                                marginTop: 16,
                                marginBottom: 8,
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <button
                                  onClick={() => setCurrentPage(p => p - 1)}
                                  disabled={currentPage === 1}
                                  className="btn btn-secondary btn-sm"
                                >
                                  Previous
                                </button>
                                <span style={{ fontSize: 13, color: "#555" }}>
                                  Page {currentPage} of {totalPages || 1}
                                </span>
                                <button
                                  onClick={() => setCurrentPage(p => p + 1)}
                                  disabled={currentPage === totalPages || totalPages === 0}
                                  className="btn btn-secondary btn-sm"
                                >
                                  Next
                                </button>
                              </div>
                              <button
                                onClick={exportToExcel}
                                className="btn btn-success"
                                disabled={!isDataFetched || !stockData || stockData.length === 0}
                              >
                                Export to Excel
                              </button>
                            </div>
                          </>
                        )
                    )}

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="kt_app_footer" className="app-footer">
        <div className="app-container container-fluid d-flex flex-column flex-md-row flex-center flex-md-stack py-3">
          <div className="text-gray-900 order-2 order-md-1">
            <span className="text-muted fw-semibold me-1">2025&copy;</span>
            <a href="#" className="text-gray-800 text-hover-primary">WineShop</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Salesreport;
